import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { ArrowLeft, CalendarClock, Check, Pencil, PlusSquare, Trash2 } from 'lucide-react';
import eventsApi from 'api/requests/events';
import sessionsApi from 'api/requests/sessions';
import Button from 'components/_UI/Button';
import Loader from 'components/_UI/Loader';
import Modal from 'components/_UI/Modal';
import Text from 'components/_UI/Text';
import { useEvent } from 'context/events/Event';
import { EVENT_LAYOUTS, formatDay, groupSessionsByDay, kindLabel, ticketProducts } from 'helpers/eventSessions';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import SessionForm from '../schedule/SessionForm';

const LAYOUTS = [
    {
        value: EVENT_LAYOUTS.DEFAULT,
        title: 'Padrão',
        description: 'A página de evento de sempre: descrição, ingressos e informações do local.',
    },
    {
        value: EVENT_LAYOUTS.SCHEDULE,
        title: 'Programação por sessões',
        description: 'Adiciona a aba Programação com palestras e minicursos, e mostra em cada sessão quais ingressos dão acesso.',
    },
];

export default function ScheduleSection({ data, loading, onBack, toast, onSuccess }) {
    const { event, setEvent } = useEvent();
    const [changingLayout, setChangingLayout] = useState(false);
    const [modal, setModal] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const layout = event?.layout || EVENT_LAYOUTS.DEFAULT;
    const isSchedule = layout === EVENT_LAYOUTS.SCHEDULE;
    const sessions = data || [];
    const days = groupSessionsByDay(sessions);
    const tickets = ticketProducts(event);
    const ticketName = id => tickets.find(item => item.id === id)?.name;

    async function chooseLayout(value) {
        if (value === layout || changingLayout) {
            return;
        }

        setChangingLayout(true);

        try {
            await sessionsApi.updateLayout(event.slug, value);
            const { data: fresh } = await eventsApi.getBySlug(event.slug);
            setEvent(fresh?.data);
            toast.success('Layout atualizado!', { position: 'top-center' });
        } catch (error) {
            toast.error('Não foi possível trocar o layout.', { position: 'top-center' });
        } finally {
            setChangingLayout(false);
        }
    }

    async function refreshEvent() {
        try {
            const { data: fresh } = await eventsApi.getBySlug(event.slug);
            setEvent(fresh?.data);
        } catch (error) {
            // A lista de sessões já foi recarregada; o evento atualiza no próximo acesso.
        }
    }

    function handleSuccess(kind) {
        setModal(null);
        toast.success(kind === 'editSession' ? 'Sessão atualizada!' : 'Sessão criada!', { position: 'top-center' });
        onSuccess();
        refreshEvent();
    }

    async function deleteSession(id) {
        setDeleting(true);

        try {
            await sessionsApi.delete(event.slug, id);
            setModal(null);
            toast.success('Sessão excluída.', { position: 'top-center' });
            onSuccess();
            refreshEvent();
        } catch (error) {
            toast.error('Não foi possível excluir a sessão.', { position: 'top-center' });
        } finally {
            setDeleting(false);
        }
    }

    return (
        <Wrapper>
            <BackRow
                onClick={onBack}
                role='button'
                tabIndex={0}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onBack()}
            >
                <BackIconWrap>
                    <ArrowLeft size={18} color={panelAccent(colors.secondaryPurple)} />
                </BackIconWrap>
                <BackLabel>Voltar</BackLabel>
            </BackRow>

            <SectionCard>
                <HeaderLeft>
                    <IconBadge>
                        <CalendarClock size={20} color={panelAccent(colors.occasionalPurple)} />
                    </IconBadge>
                    <HeaderText>
                        <SectionTitle>Layout e programação</SectionTitle>
                        <SectionDescription>
                            Escolha como a página do seu evento é montada. Para semanas acadêmicas, congressos e festivais com várias sessões, use a programação.
                        </SectionDescription>
                    </HeaderText>
                </HeaderLeft>

                <Divider />

                <LayoutGrid role='radiogroup' aria-label='Layout da página do evento'>
                    {LAYOUTS.map(item => {
                        const selected = layout === item.value;

                        return (
                            <LayoutCard
                                key={item.value}
                                type='button'
                                role='radio'
                                aria-checked={selected}
                                $selected={selected}
                                disabled={changingLayout}
                                onClick={() => chooseLayout(item.value)}
                            >
                                <LayoutTitle>
                                    {item.title}
                                    {selected && <Check size={16} strokeWidth={3} />}
                                </LayoutTitle>
                                <LayoutText>{item.description}</LayoutText>
                            </LayoutCard>
                        );
                    })}
                </LayoutGrid>

                {!isSchedule && sessions.length > 0 && (
                    <Note>
                        Há {sessions.length} {sessions.length === 1 ? 'sessão guardada' : 'sessões guardadas'}. Elas voltam a aparecer na página quando você escolher a programação.
                    </Note>
                )}
            </SectionCard>

            {isSchedule && (
                <SectionCard $spaced>
                    <SectionHeader>
                        <HeaderText>
                            <TitleRow>
                                <SectionTitle>Sessões</SectionTitle>
                                {sessions.length > 0 && <CountBadge>{sessions.length}</CountBadge>}
                            </TitleRow>
                            <SectionDescription>
                                A página pública só troca de layout quando houver pelo menos uma sessão.
                            </SectionDescription>
                        </HeaderText>

                        <Button variant='featured' category='squired' onClick={() => setModal({ type: 'form' })}>
                            <PlusSquare color={colors.neutralLight} size={16} />
                            <Text color={colors.neutralLight} paddingBottom={0} marginLeft={'6px'}>
                                Adicionar sessão
                            </Text>
                        </Button>
                    </SectionHeader>

                    <Divider />

                    {loading ? (
                        <LoaderWrap><Loader /></LoaderWrap>
                    ) : days.length > 0 ? (
                        days.map(day => {
                            const { weekday, label } = formatDay(day.date);

                            return (
                                <DayBlock key={day.date}>
                                    <DayTitle>{weekday}, {label}</DayTitle>
                                    {day.items.map(session => (
                                        <SessionRow key={session.id}>
                                            <RowTime>
                                                <b>{session.start}</b>
                                                {session.end && <span>{session.end}</span>}
                                            </RowTime>

                                            <RowBody>
                                                <RowMeta>
                                                    <Kind>{kindLabel(session.kind)}</Kind>
                                                    {session.location && <span>{session.location}</span>}
                                                    {session.speaker && <span>{session.speaker}</span>}
                                                </RowMeta>
                                                <RowTitle>{session.title}</RowTitle>
                                                <RowAccess>
                                                    {session.open_access
                                                        ? 'Qualquer ingresso do evento'
                                                        : session.product_ids?.length
                                                            ? session.product_ids.map(ticketName).filter(Boolean).join(' · ')
                                                            : 'Nenhum ingresso ligado'}
                                                </RowAccess>
                                            </RowBody>

                                            <RowActions>
                                                <IconButton
                                                    type='button'
                                                    aria-label={`Editar ${session.title}`}
                                                    onClick={() => setModal({ type: 'form', item: session })}
                                                >
                                                    <Pencil size={18} color={colors.primaryLight} />
                                                </IconButton>
                                                <IconButton
                                                    type='button'
                                                    aria-label={`Excluir ${session.title}`}
                                                    onClick={() => setModal({ type: 'delete', item: session })}
                                                >
                                                    <Trash2 size={18} color={colors.danger} />
                                                </IconButton>
                                            </RowActions>
                                        </SessionRow>
                                    ))}
                                </DayBlock>
                            );
                        })
                    ) : (
                        <EmptyState>
                            <Text color={colors.night} textAlign='center' paddingBottom={0} name='small'>
                                Nenhuma sessão cadastrada ainda.
                            </Text>
                            <EmptyHint>
                                Cadastre as palestras e minicursos e escolha quais ingressos liberam cada uma.
                            </EmptyHint>
                        </EmptyState>
                    )}
                </SectionCard>
            )}

            {modal?.type === 'form' && (
                <SessionForm
                    edit={modal.item}
                    defaultDate={days[days.length - 1]?.date}
                    onClose={() => setModal(null)}
                    onSuccess={handleSuccess}
                />
            )}

            {modal?.type === 'delete' && (
                <Modal isOpen onClose={() => setModal(null)}>
                    <Text>Tem certeza que deseja excluir a sessão "{modal.item.title}"?</Text>
                    {deleting ? (
                        <Loader />
                    ) : (
                        <ModalActions>
                            <Button onClick={() => setModal(null)} variant='light'>Cancelar</Button>
                            <Button onClick={() => deleteSession(modal.item.id)}>Confirmar</Button>
                        </ModalActions>
                    )}
                </Modal>
            )}
        </Wrapper>
    );
}

const slideIn = keyframes`
    from { opacity: 0; transform: translateX(16px); }
    to { opacity: 1; transform: translateX(0); }
`;

const Wrapper = styled.div`
    animation: ${slideIn} 0.25s ease-out;
`;

const BackRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 6px 12px 6px 6px;
    margin-bottom: 16px;
    width: fit-content;
    border-radius: 10px;
    outline: none;
    transition: background 0.15s ease;

    &:hover { background: ${colors.neutralDark}; }
    &:focus-visible { box-shadow: 0 0 0 2px ${panelAccentAlpha(colors.secondaryPurple, '44')}; }
`;

const BackIconWrap = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BackLabel = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 500;
    color: ${colors.nightDark};
`;

const SectionCard = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 24px;
    border: 1px solid ${colors.night}11;
    box-shadow: 0 1px 8px ${colors.night}08;
    margin-top: ${({ $spaced }) => ($spaced ? '16px' : 0)};
`;

const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 14px;
`;

const IconBadge = styled.div`
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 10px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '12')};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SectionTitle = styled.span`
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const CountBadge = styled.span`
    font-size: ${fonts.sizes.mini};
    font-weight: 600;
    color: ${colors.neutralLight};
    background: ${panelAccent(colors.secondaryPurple)};
    padding: 2px 8px;
    border-radius: 20px;
    line-height: 1.4;
`;

const SectionDescription = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    line-height: 1.5;
`;

const Divider = styled.div`
    height: 1px;
    background: ${colors.night}11;
    margin: 20px 0;
`;

const LayoutGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const LayoutCard = styled.button`
    display: flex;
    flex-direction: column;
    gap: 6px;
    text-align: left;
    padding: 16px;
    border-radius: 12px;
    cursor: pointer;
    font-family: inherit;
    background: ${({ $selected }) => ($selected ? panelAccentAlpha(colors.occasionalPurple, '0D') : colors.neutralLight)};
    border: 2px solid ${({ $selected }) => ($selected ? panelAccent(colors.occasionalPurple) : `${colors.night}22`)};

    &:disabled { opacity: 0.6; cursor: wait; }
    &:focus-visible { box-shadow: 0 0 0 2px ${panelAccentAlpha(colors.secondaryPurple, '44')}; }
`;

const LayoutTitle = styled.span`
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const LayoutText = styled.span`
    font-size: ${fonts.sizes.small};
    line-height: 1.5;
    color: ${colors.night};
`;

const Note = styled.p`
    margin: 14px 0 0;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
`;

const LoaderWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 160px;
`;

const DayBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;
`;

const DayTitle = styled.h3`
    margin: 0 0 4px;
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    text-transform: capitalize;
    color: ${panelAccent(colors.occasionalPurple)};
`;

const SessionRow = styled.div`
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr) auto;
    gap: 12px;
    align-items: start;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1px solid ${colors.night}18;
`;

const RowTime = styled.div`
    display: flex;
    flex-direction: column;
    font-variant-numeric: tabular-nums;

    b { font-size: ${fonts.sizes.highlight}; color: ${colors.nightDark}; }
    span { font-size: ${fonts.sizes.small}; color: ${colors.night}; }
`;

const RowBody = styled.div`
    min-width: 0;
`;

const RowMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    align-items: center;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
`;

const Kind = styled.span`
    padding: 1px 8px;
    border-radius: 6px;
    font-weight: 600;
    background: ${panelAccentAlpha(colors.occasionalPurple, '14')};
    color: ${panelAccent(colors.occasionalPurple)};
`;

const RowTitle = styled.div`
    margin-top: 4px;
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const RowAccess = styled.div`
    margin-top: 4px;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
`;

const RowActions = styled.div`
    display: flex;
    gap: 4px;
`;

const IconButton = styled.button`
    background: none;
    border: 0;
    padding: 6px;
    border-radius: 8px;
    cursor: pointer;

    &:hover { background: ${colors.neutralDark}; }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    border: 2px dashed ${colors.night}22;
    border-radius: 12px;
    background: ${colors.neutralDark}88;
`;

const EmptyHint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
    margin-top: 6px;
    text-align: center;
`;

const ModalActions = styled(Column)`
    margin-top: 15px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        gap: 5px;
    }
`;
